import type { CornersDto, LatLngDto, IXYPair, AlignmentDto } from './PolygonUtil';

/**
 * Converts coordinates between a local rectangular bounding box and a rotated WGS84 quadrilateral.
 *
 * The conversion uses a precomputed affine transform so repeated local-to-world and
 * world-to-local lookups can be performed quickly without rebuilding the matrix each call.
 */
export class CoordinateUtil {
    private local: { minX: number; minY: number; maxX: number; maxY: number };
    private wgs: CornersDto;
    private cartesian: boolean;
    private lngVectorU: number;
    private lngVectorV: number;
    private latVectorU: number;
    private latVectorV: number;
    private det: number;

    /**
     * Creates a coordinate transformer from a local bounding box and a WGS84 corner set.
     *
     * @param localBBox - `[minX, minY, maxX, maxY]` values in the local coordinate system.
     * @param wgs84Corners - The quadrilateral corners in geographic coordinates: `{ tl, tr, br, bl }`.
     * @param cartesian - `true` when local Y increases upward; `false` when it follows image-space coordinates.
     */
    constructor(localBBox: number[], wgs84Corners: CornersDto, cartesian?: boolean);

    /**
     * Creates a coordinate transformer from a predefined alignment definition.
     *
     * @param mapAlign - Alignment metadata containing the local bounds and mapped corner coordinates.
     * @param cartesian - `true` when local Y increases upward; `false` when it follows image-space coordinates.
     */
    constructor(mapAlign: AlignmentDto, cartesian?: boolean);

    constructor(
        arg1: number[] | AlignmentDto,
        arg2: CornersDto | boolean = true,
        cartesian: boolean = true,
    ) {
        if (Array.isArray(arg1)) {
            if (arg1.length !== 4) {
                throw new Error('Invalid bbox. Expected [minX, minY, maxX, maxY].');
            }

            if (typeof arg2 === 'boolean') {
                throw new Error('WGS84 corners are required when initializing from a local bbox.');
            }

            const [minX, minY, maxX, maxY] = arg1;
            this.local = { minX, minY, maxX, maxY };
            this.wgs = arg2;
            this.cartesian = cartesian;
        } else {
            const [minX, minY, maxX, maxY] = arg1.bbox ?? [0, 0, arg1.size.width, arg1.size.height];
            this.local = { minX, minY, maxX, maxY };
            this.wgs = arg1.corners;
            this.cartesian = arg2 !== false;
        }

        this.lngVectorU = this.wgs.tr.lng - this.wgs.tl.lng;
        this.lngVectorV = this.wgs.bl.lng - this.wgs.tl.lng;
        this.latVectorU = this.wgs.tr.lat - this.wgs.tl.lat;
        this.latVectorV = this.wgs.bl.lat - this.wgs.tl.lat;

        // det = (B * F) - (C * E)
        this.det = this.lngVectorU * this.latVectorV - this.lngVectorV * this.latVectorU;
    }

    /**
     * Checks whether a value matches the XY point shape.
     */
    private static isXYPair(value: unknown): value is IXYPair {
        return typeof value === 'object' && value !== null && 'x' in value && 'y' in value;
    }

    /**
     * Checks whether a value matches the lat/lng point shape.
     */
    private static isLatLngDto(value: unknown): value is LatLngDto {
        return typeof value === 'object' && value !== null && 'lat' in value && 'lng' in value;
    }

    /**
     * Converts local XY coordinates to WGS84 latitude/longitude.
     *
     * @overload
     * @param coord - Local coordinate as `[x, y]` or `{ x, y }`.
     * @returns The geographic point in `{ lat, lng }` form.
     */
    xyToLatLng(coord: number[] | IXYPair): LatLngDto;

    /**
     * Converts a local XY point to WGS84 latitude/longitude.
     *
     * @param x - Local X coordinate.
     * @param y - Local Y coordinate.
     * @returns The geographic point in `{ lat, lng }` form.
     */
    xyToLatLng(x: number, y: number): LatLngDto;
    xyToLatLng(x: number | number[] | IXYPair, y?: number): LatLngDto {
        if (y === undefined) {
            if (Array.isArray(x)) return this.xyToLatLng(x[0], x[1]);
            if (CoordinateUtil.isXYPair(x)) return this.xyToLatLng(x.x, x.y);
            throw new Error('Invalid XY coordinate input. Expected [x, y] or { x, y }.');
        }
        if (typeof x !== 'number') throw new Error('Invalid XY coordinate input.');

        const u = (x - this.local.minX) / (this.local.maxX - this.local.minX);
        const v = this.cartesian
            ? (this.local.maxY - y) / (this.local.maxY - this.local.minY)
            : (y - this.local.minY) / (this.local.maxY - this.local.minY);

        const lng = this.wgs.tl.lng + u * this.lngVectorU + v * this.lngVectorV;
        const lat = this.wgs.tl.lat + u * this.latVectorU + v * this.latVectorV;
        return { lat, lng };
    }

    /**
     * Converts WGS84 latitude/longitude to local XY coordinates.
     *
     * @overload
     * @param coord - Geographic coordinate as `[lng, lat]` or `{ lat, lng }`.
     * @returns The converted local point in `{ x, y }` form.
     */
    latLngToXY(coord: number[] | LatLngDto): IXYPair;

    /**
     * Converts WGS84 latitude/longitude to local XY coordinates.
     *
     * @param lat - Latitude value in decimal degrees.
     * @param lng - Longitude value in decimal degrees.
     * @returns The converted local point in `{ x, y }` form.
     */
    latLngToXY(lat: number, lng: number): IXYPair;
    latLngToXY(lat: number | number[] | LatLngDto, lng?: number): IXYPair {
        if (this.det === 0) throw new Error('Collinear WGS84 points.');
        if (lng === undefined) {
            if (Array.isArray(lat)) return this.latLngToXY(lat[1], lat[0]);
            if (CoordinateUtil.isLatLngDto(lat)) return this.latLngToXY(lat.lat, lat.lng);
            throw new Error('Invalid lat/lng coordinate input.');
        }
        if (typeof lat !== 'number') throw new Error('Invalid latitude value.');

        const dLng = lng - this.wgs.tl.lng;
        const dLat = lat - this.wgs.tl.lat;

        const u = (dLng * this.latVectorV - dLat * this.lngVectorV) / this.det;
        const v = (this.lngVectorU * dLat - this.latVectorU * dLng) / this.det;

        const x = this.local.minX + u * (this.local.maxX - this.local.minX);
        const y = this.cartesian
            ? this.local.maxY - v * (this.local.maxY - this.local.minY)
            : this.local.minY + v * (this.local.maxY - this.local.minY);

        return { x, y };
    }
}
